/**
 * @fileoverview Context for the middleware
 *
 * @example
 * // Usage:
 * import { MiddlewareContext } from './MiddlewareContext'
 *
 * function Component() {
 *   // ...
 * }
 */

import { createContext } from 'react'

/**
 * @interface Middleware
 * @description The interface for the middleware
 * @property {string} assignmentId - The assignment ID
 * @property {Novoic.AnyQuestion} question - The question
 * @property {boolean} isLegacyBattery - Whether the battery is legacy
 * @property {Record<string, unknown>} errors - The errors
 * @property {boolean} isExpired - Whether the assignment is expired
 * @property {boolean} standalone - Whether the assignment is standalone
 * @property {() => Promise<void>} onTimeout - The function to call when the assignment times out
 * @property {(language: string) => Promise<void>} onLanguageChange - The function to call when the language changes
 * @property {(questionID: string, answer: Record<string, unknown>) => Promise<Record<string, unknown> | void>} submit - The function to call when the user submits an answer
 * @property {() => Promise<void>} exit - The function to call when the user exits the assignment
 */
export interface Middleware {
    assignmentId: string
    question?: Novoic.AnyQuestion
    isLegacyBattery: boolean
    errors?: Record<string, unknown>
    isExpired: boolean
    standalone?: boolean
    onTimeout?: () => Promise<void>
    onLanguageChange?: (language: string) => Promise<void>
    submit: (
        questionID: string,
        answer: Record<string, unknown>
    ) => Promise<Record<string, unknown> | void>
    exit: () => Promise<void>
}

export const MiddlewareContext = createContext<Middleware>(undefined)
